import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import clsx from 'clsx';
import { motion } from 'framer-motion';
import Button from '@/components/Button';
import config from '@/config';

const APP_STORE_URL = 'https://apps.apple.com/us/app/sk-club-frikd-up-fishies/id6755168106';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.minnerlabs.FrikdUpFishies';

const FloatingBubble = ({ className, delay = 0, duration = 4 }) => (
  <motion.div
    className={clsx('absolute rounded-full opacity-20 pointer-events-none', className)}
    animate={{ y: [0, -20, 0], scale: [1, 1.1, 1] }}
    transition={{ duration, delay, repeat: Infinity, ease: 'easeInOut' }}
  />
);

const SwimmingFish = ({ src, className, delay = 0, duration = 8, flip = false }) => (
  <motion.div
    className={clsx('absolute opacity-90 pointer-events-none', className)}
    animate={{ x: [0, 30, 0, -10, 0], y: [0, -8, 4, -4, 0], rotate: flip ? [-3, 3, -3] : [3, -3, 3] }}
    transition={{ duration, delay, repeat: Infinity, ease: 'easeInOut' }}
  >
    <img src={src} alt='' className={clsx('w-full h-full', flip && 'scale-x-[-1]')} />
  </motion.div>
);

const SectionUnderline = ({ className = 'max-w-[140px] md:max-w-[220px] mt-4 mx-auto' }) => (
  <div className={className}>
    <Image src='/accents/underline.png' alt='' width={722} height={121} />
  </div>
);

const features = [
  {
    title: 'Hand-drawn Fish',
    body: '40+ unique fish species across 4 rarity tiers. From starter goldfish to legendary space bettas, every one drawn by hand.',
  },
  {
    title: 'Care System',
    body: 'Feed. Clean. Play. Cure. Each action has its own cooldown and reward. Skip a day and your fish will let you know.',
  },
  {
    title: 'Tank Customization',
    body: 'Three tank sizes, colored lids, substrates, and 80+ decorations. Build a vibe, from minimalist zen to total chaos.',
  },
  {
    title: 'Personality Traits',
    body: '8 personality traits affect how each fish behaves. Lazy. Energetic. Shy. Lucky. Lucky ones drop more coins. You can guess what lazy ones do.',
  },
  {
    title: '30+ Achievements',
    body: 'Track milestones across collection, care, decoration, and dedication. Every achievement earns a one-of-a-kind badge.',
  },
  {
    title: 'Daily Rewards',
    body: 'Show up. Get coins. A 7-day streak cycle with multipliers. Break the streak and start over, no take-backs.',
  },
  {
    title: 'Offline-First',
    body: 'Everything saved locally. No account required. Optional cloud sync if you want to keep your tank across devices.',
  },
  {
    title: 'Coin Economy',
    body: 'Earn coins from care actions, taps, and passive income when your fish are happy. Spend them on more fish and stuff.',
  },
];

const fishLineup = [
  { src: '/sk-club/og-assets/goldfish.png', name: 'Goldfish', rarity: 'Starter' },
  { src: '/sk-club/og-assets/neon_tetra.png', name: 'Neon Tetra', rarity: 'Common' },
  { src: '/sk-club/og-assets/clownfish.png', name: 'Clownfish', rarity: 'Common' },
  { src: '/sk-club/og-assets/blue-tang.png', name: 'Blue Tang', rarity: 'Uncommon' },
  { src: '/sk-club/og-assets/moorish_idol.png', name: 'Moorish Idol', rarity: 'Uncommon' },
  { src: '/sk-club/og-assets/pufferfish.png', name: 'Pufferfish', rarity: 'Uncommon' },
  { src: '/sk-club/og-assets/seahorse.png', name: 'Seahorse', rarity: 'Rare' },
  { src: '/sk-club/og-assets/golden-betta.png', name: 'Golden Betta', rarity: 'Rare' },
  { src: '/sk-club/og-assets/golden_seahorse.png', name: 'Golden Seahorse', rarity: 'Legendary' },
  { src: '/sk-club/og-assets/space_betta.png', name: 'Space Betta', rarity: 'Legendary' },
];

const rarityClasses = {
  Starter: 'bg-gray-700/50 text-gray-200 border-gray-600/50',
  Common: 'bg-primary-900/50 text-primary-200 border-primary-700/50',
  Uncommon: 'bg-blue-900/50 text-blue-200 border-blue-700/50',
  Rare: 'bg-purple-900/50 text-purple-200 border-purple-700/50',
  Legendary: 'bg-secondary-900/60 text-secondary-200 border-secondary-700/50',
};

const StoreBadges = ({ size = 'lg' }) => (
  <div className='flex flex-wrap items-center justify-center lg:justify-start gap-4'>
    <motion.a
      href={APP_STORE_URL}
      target='_blank'
      rel='noopener noreferrer'
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className='inline-block transition-opacity hover:opacity-80'
    >
      <img
        src='/badges/app-store-badge.svg'
        alt='Download on the App Store'
        className={clsx('w-auto', size === 'lg' ? 'h-[60px]' : 'h-[48px]')}
      />
    </motion.a>
    <motion.a
      href={PLAY_STORE_URL}
      target='_blank'
      rel='noopener noreferrer'
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      className='inline-block transition-opacity hover:opacity-80'
    >
      <img
        src='/badges/google-play-badge.svg'
        alt='Get it on Google Play'
        className={clsx('w-auto', size === 'lg' ? 'h-[60px]' : 'h-[48px]')}
      />
    </motion.a>
  </div>
);

export default function FrikdUpFishiesDetail() {
  return (
    <>
      {/* Hero */}
      <section className='relative overflow-hidden bg-gray-900'>
        <div className='absolute inset-0 bg-gradient-to-b from-[#0a1929] via-[#0f2c4a] to-[#0e4d5e] opacity-95' />
        <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-500/20 via-transparent to-transparent' />
        <FloatingBubble className='w-32 h-32 bg-white top-10 left-10' delay={0} duration={5} />
        <FloatingBubble className='w-20 h-20 bg-white top-1/4 right-20' delay={1.2} />
        <FloatingBubble className='w-16 h-16 bg-white bottom-1/4 left-32' delay={2} />
        <FloatingBubble className='w-24 h-24 bg-primary-500 top-1/3 right-10' delay={0.6} duration={6} />
        <SwimmingFish src='/sk-club/og-assets/clownfish.png' className='w-24 h-24 top-32 left-[8%] opacity-30 hidden md:block' delay={0} duration={9} />
        <SwimmingFish src='/sk-club/og-assets/blue-tang.png' className='w-32 h-32 bottom-24 right-[10%] opacity-30 hidden md:block' delay={2} duration={11} flip />

        <div className='relative z-10 px-4 mx-auto lg:px-8 max-w-7xl pt-20 md:pt-28 pb-12 md:pb-16'>
          <div className='mb-6 flex items-center gap-2 text-sm text-neutral-400'>
            <Link href='/sk-club'>
              <a className='hover:text-primary-400 transition-colors'>SK Club</a>
            </Link>
            <span>/</span>
            <span className='text-neutral-300'>Frik'd Up Fishies</span>
          </div>

          <div className='grid lg:grid-cols-2 gap-10 lg:gap-16 items-center'>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className='text-center lg:text-left'
            >
              <div className='mb-6 mt-6 max-w-[140px] md:max-w-[160px] mx-auto lg:mx-0'>
                <Image src='/sk-club/logo-frikd.png' alt="Frik'd Up Fishies" width={500} height={339} priority className='w-full h-auto drop-shadow-2xl' />
              </div>

              <h1 className='text-3xl font-extrabold text-neutral-200 md:text-5xl leading-tight'>
                Kitties taking care of fish.
                <br />
                <span className='text-primary-500'>What could go wrong?</span>
              </h1>

              <div className='max-w-[200px] md:max-w-[300px] mt-4 mx-auto lg:mx-0'>
                <Image src='/accents/underline.png' alt='' width={722} height={121} />
              </div>

              <p className='text-neutral-400 text-base md:text-lg mt-6 mb-8 max-w-xl mx-auto lg:mx-0 leading-relaxed'>
                A virtual pet aquarium for your phone. Build your tank. Collect hand-drawn fish. Let your kitties run the place. They promise they're not
                hungry. Mostly chill, occasionally chaotic, always shitty in the best way.
              </p>

              <StoreBadges />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className='relative'
            >
              <div className='absolute -inset-6 bg-gradient-to-br from-primary-500/30 via-secondary-500/20 to-blue-500/30 rounded-[2.5rem] blur-2xl' />
              <div className='relative rounded-2xl overflow-hidden shadow-2xl border-2 border-gray-700/50'>
                <video autoPlay loop muted playsInline className='w-full aspect-[4/3] object-cover'>
                  <source src='/sk-club/frikd-up-fishies-v2.mov' type='video/mp4' />
                </video>
                <div className='absolute inset-0 bg-gradient-to-t from-gray-900/60 via-transparent to-transparent pointer-events-none' />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className='relative overflow-hidden bg-gray-800 py-20 md:py-28'>
        <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_center_top,_var(--tw-gradient-stops))] from-primary-500/10 via-transparent to-transparent' />
        <FloatingBubble className='w-20 h-20 bg-primary-500 top-20 left-10' delay={0} duration={6} />
        <FloatingBubble className='w-16 h-16 bg-secondary-500 bottom-32 right-20' delay={1} />

        <div className='relative z-10 px-4 mx-auto lg:px-8 max-w-7xl'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className='text-center mb-14'
          >
            <h2 className='text-2xl font-extrabold text-neutral-300 md:text-4xl'>
              Built to <span className='text-primary-500'>Frik You Up</span>
            </h2>
            <SectionUnderline />
            <p className='mt-4 text-base md:text-lg text-neutral-400 max-w-2xl mx-auto'>
              All the systems running underneath this dumb little fish tank.
            </p>
          </motion.div>

          <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-5'>
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className='relative group'
              >
                <div className='relative h-full p-6 rounded-xl bg-gray-900 border border-gray-700/60 hover:border-primary-500/40 transition-colors'>
                  <h3 className='font-bold text-xl text-neutral-100 mb-2'>{feature.title}</h3>
                  <p className='text-neutral-400 text-sm leading-relaxed'>{feature.body}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Fish collection */}
      <section className='relative overflow-hidden bg-gradient-to-b from-[#0a1929] to-[#0e4d5e] py-20 md:py-28'>
        <FloatingBubble className='w-32 h-32 bg-white top-10 left-1/4' delay={0} duration={6} />
        <FloatingBubble className='w-20 h-20 bg-white bottom-20 right-1/4' delay={1} duration={5} />

        <div className='relative z-10 px-4 mx-auto lg:px-8 max-w-7xl'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className='text-center mb-14'
          >
            <h2 className='text-2xl font-extrabold text-neutral-200 md:text-4xl'>
              Collect the <span className='text-primary-500'>Frikd Up</span> Fishies
            </h2>
            <SectionUnderline />
            <p className='mt-4 text-base md:text-lg text-neutral-300/80 max-w-2xl mx-auto'>
              40+ species across four rarity tiers. Some are common, some are absurd, all of them are hand-drawn.
            </p>
          </motion.div>

          <div className='grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4 max-w-5xl mx-auto'>
            {fishLineup.map((fish, i) => (
              <motion.div
                key={fish.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className='group relative aspect-square rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 p-4 flex flex-col items-center justify-center hover:bg-white/10 hover:border-white/20 transition-all'
              >
                <div className='flex-1 w-full flex items-center justify-center'>
                  <motion.img
                    src={fish.src}
                    alt={fish.name}
                    className='max-w-full max-h-full w-auto h-auto object-contain group-hover:scale-110 transition-transform'
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 3, delay: i * 0.2, repeat: Infinity, ease: 'easeInOut' }}
                  />
                </div>
                <div className='w-full mt-2 text-center'>
                  <div className='text-neutral-100 font-bold text-sm truncate'>{fish.name}</div>
                  <div className={clsx('inline-block mt-1 px-2 py-0.5 text-[10px] rounded-full uppercase tracking-wider border', rarityClasses[fish.rarity])}>
                    {fish.rarity}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className='text-center mt-10'>
            <p className='text-neutral-300/70 text-sm'>...and 30+ more, including legendary variants only the lucky and patient will ever see.</p>
          </div>
        </div>
      </section>

      {/* TREATS integration */}
      {config.skClub.exchangeEnabled && (
      <section className='relative overflow-hidden bg-gray-800 py-20 md:py-24'>
        <div className='absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-secondary-500/15 via-transparent to-transparent' />
        <div className='relative z-10 px-4 mx-auto lg:px-8 max-w-5xl'>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className='relative rounded-2xl overflow-hidden border-2 border-gray-700/50 bg-gray-900'
          >
            <div className='absolute inset-0 bg-gradient-to-br from-primary-500/15 via-transparent to-secondary-500/15' />
            <div className='relative p-8 md:p-12 grid md:grid-cols-[auto_1fr_auto] gap-8 items-center'>
              <div className='flex-shrink-0 mx-auto md:mx-0'>
                <div className='relative w-24 h-24 md:w-32 md:h-32 rounded-2xl overflow-hidden shadow-xl border-2 border-gray-700/60'>
                  <Image src='/sk-club/frikd-app-icon.png' alt='' width={128} height={128} className='w-full h-full' />
                </div>
              </div>
              <div className='text-center md:text-left'>
                <h3 className='text-xl md:text-3xl font-extrabold text-neutral-200 mb-3'>
                  Hold Shitty Kitties? <span className='text-primary-500'>Trade $TREATS for Coins</span>
                </h3>
                <p className='text-neutral-400 leading-relaxed'>
                  Link your wallet to your SK Club account and swap $TREATS for in-game coins (and back). Holders, this is your perk.
                </p>
              </div>
              <Button link href='/sk-club' className='whitespace-nowrap'>
                Open SK Club
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
      )}

      {/* Final CTA */}
      <section className='relative overflow-hidden bg-gradient-to-b from-gray-900 to-[#0e4d5e] py-20 md:py-28'>
        <FloatingBubble className='w-24 h-24 bg-white top-10 right-20' delay={0} duration={5} />
        <FloatingBubble className='w-16 h-16 bg-white bottom-20 left-20' delay={1} />
        <SwimmingFish src='/sk-club/og-assets/golden-betta.png' className='w-32 h-32 top-20 left-[10%] opacity-30 hidden md:block' delay={0} duration={10} />
        <SwimmingFish src='/sk-club/og-assets/space_betta.png' className='w-28 h-28 bottom-10 right-[12%] opacity-30 hidden md:block' delay={2} duration={9} flip />

        <div className='relative z-10 px-4 mx-auto lg:px-8 max-w-3xl text-center'>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className='text-4xl font-extrabold text-neutral-200 md:text-6xl'
          >
            Get <span className='text-primary-500'>Frik'd Up</span>.
          </motion.h2>
          <SectionUnderline className='max-w-[160px] md:max-w-[260px] mt-4 mx-auto' />
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className='text-neutral-400 text-base md:text-lg mt-6 mb-10 max-w-xl mx-auto'
          >
            Free to play. No accounts, no ads in your face, just fish.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className='flex justify-center'
          >
            <StoreBadges />
          </motion.div>
        </div>
      </section>
    </>
  );
}
